import React, { useMemo } from 'react';
import { Spin } from 'antd';
import { useNavigate, useParams, useSearchParams, Navigate } from 'react-router-dom';
import { PrintPreviewChrome } from '@/components/print/PrintPreviewChrome';
import type { CompanyPrintInfo } from '@/components/sales-invoice/PrintableSalesInvoice';
import { useDeliveryPrintData } from '@/hooks/useDelivery';
import { useSystemSettings } from '@/hooks/useSettings';
import { DeliveryPackingListPrintView } from '@/components/sales/DeliveryPackingListPrintView';
import { DeliveryCommercialInvoicePrintView } from '@/components/sales/DeliveryCommercialInvoicePrintView';
import {
  DeliveryNotePrintView,
  type DeliveryPrintLetterheadVariant,
} from '@/components/sales/DeliveryNotePrintView';

type DeliveryPrintVariant = 'packing' | 'delivery-note' | 'commercial-invoice';

/**
 * Layout-isolated route:
 * /print/sales/delivery-notes/:id?variant=packing|delivery-note|commercial-invoice&format=letterhead|preprinted
 * User reviews the sheet, then clicks Print (no auto-print).
 */
const DeliveryPrintPreviewPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const variantRaw = searchParams.get('variant');
  const variant: DeliveryPrintVariant =
    variantRaw === 'delivery-note'
      ? 'delivery-note'
      : variantRaw === 'commercial-invoice'
        ? 'commercial-invoice'
        : 'packing';
  const formatRaw = searchParams.get('format');
  const printVariant: DeliveryPrintLetterheadVariant =
    formatRaw === 'preprinted' ? 'preprinted' : 'letterhead';

  const { data: printData, isLoading, isError } = useDeliveryPrintData(id);
  const { data: systemSettings } = useSystemSettings();

  const companyInfo: CompanyPrintInfo = useMemo(() => {
    const companySettings = systemSettings?.filter((s) => s.category === 'company') ?? [];
    const getSetting = (key: string) =>
      companySettings.find((s) => s.setting_key === key)?.setting_value ?? '';
    const trn = getSetting('tax_registration_number');
    return {
      name: getSetting('company_name') || 'Company',
      address: getSetting('company_address') || '',
      phone: getSetting('company_phone') || '',
      email: getSetting('company_email') || '',
      taxNumber: trn ? `TRN: ${trn}` : '',
    };
  }, [systemSettings]);

  const back = () => {
    if (id) navigate(`/growth/delivery-notes/${id}`);
    else navigate('/growth/delivery-notes');
  };

  if (!id) {
    return <Navigate to="/growth/delivery-notes" replace />;
  }

  const docLabel =
    variant === 'delivery-note'
      ? 'Delivery note'
      : variant === 'commercial-invoice'
        ? 'Commercial invoice'
        : 'Packing list';
  const formatLabel = printVariant === 'letterhead' ? 'with letterhead' : 'preprinted';
  const title = `${docLabel} — ${formatLabel} — print preview`;

  if (isLoading) {
    return (
      <PrintPreviewChrome title={title} onBack={back}>
        <div style={{ display: 'flex', justifyContent: 'center', padding: 120 }}>
          <Spin size="large" />
        </div>
      </PrintPreviewChrome>
    );
  }

  if (isError || !printData) {
    return (
      <PrintPreviewChrome title={title} onBack={back}>
        <div style={{ padding: 48, textAlign: 'center' }}>Could not load print data.</div>
      </PrintPreviewChrome>
    );
  }

  return (
    <PrintPreviewChrome title={title} fileName={printData.delivery_note_number} onBack={back}>
      <div className="print-doc-letter-dn">
        {variant === 'delivery-note' ? (
          <DeliveryNotePrintView
            data={printData}
            companyInfo={companyInfo}
            printVariant={printVariant}
          />
        ) : variant === 'commercial-invoice' ? (
          <DeliveryCommercialInvoicePrintView
            data={printData}
            companyInfo={companyInfo}
            printVariant={printVariant}
          />
        ) : (
          <DeliveryPackingListPrintView
            data={printData}
            companyInfo={companyInfo}
            printVariant={printVariant}
          />
        )}
      </div>
    </PrintPreviewChrome>
  );
};

export default DeliveryPrintPreviewPage;
